import { ArrowLeft, BadgeCheck, Mail } from "lucide-react";
import { CONTACT } from "../../config/contact";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_accessibility-first-5/artifacts/4w169tlv_ChatGPT%20Image%20Jan%201%2C%202026%2C%2002_13_50%20AM.png";

const features = [
  "Full keyboard navigation with a visible focus indicator on every interactive element",
  "A \u201cSkip to main content\u201d link as the first focusable element",
  "Semantic HTML landmarks, a logical heading hierarchy, and ordered lists for sequential content",
  "Text alternatives for meaningful images; decorative visuals hidden from assistive technology",
  "Colour contrast meeting or exceeding WCAG 2.2 AA ratios throughout",
  "Support for the prefers-reduced-motion setting, with pause controls for all auto-moving content",
  "Accessible forms with visible labels, error identification, suggestions, and live-region announcements",
  "Touch targets sized for comfortable operation on all devices",
  "Content that reflows without horizontal scrolling down to 320 CSS pixels and up to 400% zoom",
];

export default function AccessibilityStatement() {
  return (
    <div data-testid="accessibility-statement-page" className="min-h-screen bg-white">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="border-b border-[#0B1F3A]/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <a href="/" aria-label="Libros Consulting — Home" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-display font-bold text-lg text-[#0B1F3A] tracking-tight">Libros Consulting</span>
          </a>
          <a
            data-testid="back-to-home"
            href="/"
            className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1D4ED8] hover:text-[#0B1F3A] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to home
          </a>
        </div>
      </header>

      <main id="main-content" className="mx-auto max-w-4xl px-5 sm:px-8 py-14 sm:py-20">
        <div className="overline">Our Commitment</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0B1F3A] tracking-tighter leading-[1.08]">
          Accessibility Statement
        </h1>
        <p className="mt-5 text-[#475569] text-base sm:text-lg leading-relaxed max-w-2xl">
          Accessibility is at the heart of what Libros Consulting does. We help publishers, universities, museums,
          and enterprises make their content accessible &mdash; and we hold our own digital presence to the same
          standard.
        </p>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-[#0B1F3A] tracking-tight">Conformance status</h2>
          <p className="mt-3 text-[#475569] leading-relaxed">
            This website has been built with the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA as its
            design and engineering target. Automated evaluation (axe-core, covering WCAG 2.0, 2.1, and 2.2 A/AA
            rules) reports zero violations, and the site is regularly tested with keyboard-only navigation,
            reduced-motion settings, and responsive/zoom checks. We are committed to maintaining and improving
            this level of accessibility as the site evolves.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#00776E]/30 bg-[#00B8A9]/10 px-4 py-2 text-[13px] font-semibold text-[#00776E]">
            <BadgeCheck className="h-4 w-4" aria-hidden="true" />
            Built with WCAG 2.2 AA best practices
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-[#0B1F3A] tracking-tight">
            Accessibility features of this website
          </h2>
          <ul className="mt-4 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-[#475569] leading-relaxed">
                <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1D4ED8] shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-[#0B1F3A] tracking-tight">Feedback</h2>
          <p className="mt-3 text-[#475569] leading-relaxed">
            We welcome your feedback on the accessibility of this website. If you encounter an accessibility
            barrier, or need any content in an alternative format, please contact us &mdash; we aim to respond
            within two business days.
          </p>
          <a
            data-testid="a11y-feedback-email"
            href={`mailto:${CONTACT.generalEmail}?subject=Website%20Accessibility%20Feedback`}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-5 py-3 text-[14px] font-semibold hover:bg-[#1F5EFF] transition-colors"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Report an accessibility issue
          </a>
        </section>

        <p className="mt-14 text-[13px] text-[#475569]">Last reviewed: June 2026</p>
      </main>

      <footer className="border-t border-[#0B1F3A]/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-6 text-[13px] text-[#475569]">
          © {new Date().getFullYear()} Libros Consulting. All rights reserved. · {CONTACT.website}
        </div>
      </footer>
    </div>
  );
}
